import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Row, Col, Card, message, DatePicker, Select, AutoComplete, Modal, Space, Tabs } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { unstable_useBlocker as useBlocker } from 'react-router-dom';
import { accidentService, dictionaryService, authService } from '../../services';
import './AccidentFiling.css';

const AccidentFiling = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState({});
    const [investigators, setInvestigators] = useState([]);
    const [drawers, setDrawers] = useState([]);
    const [weatherOptions, setWeatherOptions] = useState([]);
    const [roadSurfaceOptions, setRoadSurfaceOptions] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [showDrawing, setShowDrawing] = useState(false);
    const [currentAccidentId, setCurrentAccidentId] = useState(null);
    const [hdInitOk, setHdInitOk] = useState(false); // 实际业务 accidentId 初始化成功
    const [isLoading, setIsLoading] = useState(false); // WASM 加载中
    const [loadingText, setLoadingText] = useState('加载中...');
    const [previewImages, setPreviewImages] = useState({
        vertical: null,     // 竖版
        horizontal: null,   // 横版
        scene: null,        // 实景记录图
        overhead: null      // 实景俯瞰记录图
    });
    const [savedZipData, setSavedZipData] = useState(null); // 保存的zip数据(base64)
    const [iframeReady, setIframeReady] = useState(false); // iframe已创建并开始加载
    const iframeContainerRef = React.useRef(null);
    const fileInputRef = React.useRef(null);
    const hdInitCompleteTimeRef = React.useRef(0); // hdInit 完成的时间戳，1000ms 内的 hd:close-hd 是旧 session 清理事件
    const closeDialogRef = React.useRef(false);   // 防止同时弹出多个保存对话框

    // ========== 离开页面/刷新前的未保存数据提示 ==========
    // 判定是否存在未保存的数据：
    const hasFileState = !!(files.preview || files.vertical || files.horizontal || files.scene || files.overhead || files.dataPackage);
    const hasPreviews = !!(previewImages.vertical || previewImages.horizontal || previewImages.scene || previewImages.overhead);
    const drawTimeVal = form.getFieldValue('draw_time');
    const hasUnsaved = !!(showDrawing || hasFileState || hasPreviews || drawTimeVal);

    // 浏览器刷新/关闭拦截
    useEffect(() => {
        const beforeUnloadHandler = (e) => {
            if (!hasUnsaved) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', beforeUnloadHandler);
        return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
    }, [hasUnsaved]);

    // 路由切换拦截（React Router v6.4+）
    const blocker = useBlocker(hasUnsaved);
    useEffect(() => {
        if (blocker.state === 'blocked') {
            const modal = Modal.confirm({
                title: '未保存的绘图数据',
                content: '检测到未保存的现场绘图数据，离开将丢失更改。是否放弃并离开？',
                okText: '放弃并离开',
                cancelText: '留在此页',
                onOk: () => {
                    modal.destroy();
                    blocker.proceed();
                },
                onCancel: () => {
                    modal.destroy();
                    blocker.reset();
                }
            });
        }
    }, [blocker]);

    // 设置表单默认值
    useEffect(() => {
        form.setFieldsValue({
            accident_id: Date.now().toString(), // 当前时间戳（毫秒）
            accident_time: dayjs(), // 当前时间
            arrive_time: dayjs(), // 到达事故现场时间默认当前时间
            weather: '晴',
            road_surface: '沥青'
        });
    }, [form]);

    // Modal 打开/关闭后的回调
    const handleModalAfterOpenChange = (open) => {
        console.log('[AccidentFiling] ========== Modal afterOpenChange ==========');
        console.log('[AccidentFiling] open:', open);
        console.log('[AccidentFiling] iframeContainerRef.current:', iframeContainerRef.current);

        if (open && iframeContainerRef.current) {
            // 禁用宿主页面滚动条，防止出现根文档滚动条
            try {
                document.documentElement.style.overflow = 'hidden';
                document.body.style.overflow = 'hidden';
            } catch { }

            // 动态计算头部高度，精确设置容器高度，避免 1px 溢出导致滚动条
            try {
                const modalEl = document.querySelector('.fullscreen-modal .ant-modal');
                const headerEl = modalEl ? modalEl.querySelector('.ant-modal-header') : null;
                const bodyEl = modalEl ? modalEl.querySelector('.ant-modal-body') : null;
                const headerH = headerEl ? headerEl.offsetHeight : 0;
                if (bodyEl) bodyEl.style.height = headerH ? `calc(100vh - ${headerH}px)` : '100vh';
                if (iframeContainerRef.current) iframeContainerRef.current.style.height = headerH ? `calc(100vh - ${headerH}px)` : '100vh';
            } catch { }

            // 每次打开都创建全新 iframe，确保 WASM 从干净状态初始化
            const staleIframe = document.getElementById('hd-frame');
            if (staleIframe) staleIframe.remove();
            if (typeof window.hdResetLoadState === 'function') window.hdResetLoadState();

            const iframe = document.createElement('iframe');
            iframe.id = 'hd-frame';
            iframe.setAttribute('allow', 'geolocation; clipboard-read; clipboard-write; storage-access');
            iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
            iframe.setAttribute('scrolling', 'no');
            iframe.style.cssText = 'width:100%;height:100%;border:none;margin:0;padding:0;display:block;background:#fff;position:absolute;top:0;left:0;overflow:hidden;';

            iframeContainerRef.current.appendChild(iframe);

            setTimeout(() => {
                if (typeof window.hdLoadWasm === 'function') {
                    window.hdLoadWasm();
                    setIframeReady(true);
                }
            }, 100);
        } else if (!open) {
            // Modal 关闭：移除 iframe，彻底释放 WASM 状态，下次打开走全新初始化
            const existingIframe = document.getElementById('hd-frame');
            if (existingIframe) {
                if (typeof window.hdResetLoadState === 'function') window.hdResetLoadState();
                existingIframe.remove();
            }
            setIframeReady(false);
            setHdInitOk(false);
            // 恢复页面滚动
            try {
                document.documentElement.style.overflow = '';
                document.body.style.overflow = '';
            } catch { }
        }
    };

    // ========== 提供给 WASM 的“导入图片”桥接与按钮逻辑 ==========
    const injectImageBytes = async (u8) => {
        if (!hdInitOk || !currentAccidentId) {
            message.warning('绘图未就绪，稍后再试');
            return { success: false, msg: 'not-ready' };
        }
        if (typeof window.hdAppendSceneImage !== 'function') {
            message.error('当前版本不支持导入照片');
            return { success: false, msg: 'no-api' };
        }
        try {
            const res = await window.hdAppendSceneImage(String(currentAccidentId), u8);
            if (res?.success) {
                message.success('已导入一张照片');
                return { success: true };
            } else {
                message.error('导入失败');
                return { success: false };
            }
        } catch (e) {
            console.error('injectImageBytes failed:', e);
            message.error('导入异常: ' + e.message);
            return { success: false, msg: e.message };
        }
    };

    const injectImageFromFile = async (file) => {
        if (!file) return;
        if (!/^image\//i.test(file.type)) {
            message.warning('仅支持图片文件');
            return;
        }
        if (file.size > 15 * 1024 * 1024) { // 15MB 限制
            message.warning('图片过大，请选择小于15MB的图片');
            return;
        }
        const buf = await file.arrayBuffer();
        const u8 = new Uint8Array(buf);
        await injectImageBytes(u8);
    };

    const handleImportPhotoClick = () => {
        if (!hdInitOk) {
            message.info('WASM 正在初始化，请稍候');
            return;
        }
        if (fileInputRef.current) {
            try { fileInputRef.current.value = ''; } catch { }
            fileInputRef.current.click();
        }
    };

    const handleFileInputChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (file) {
            await injectImageFromFile(file);
        }
        // 重置，避免选择同一个文件时不触发change
        try { e.target.value = ''; } catch { }
    };

    const handleImportTest = async () => {
        if (!hdInitOk) {
            message.info('WASM 正在初始化，请稍候');
            return;
        }
        // 生成一张简单的测试图片（避免引入静态资源）
        const canvas = document.createElement('canvas');
        canvas.width = 1024;
        canvas.height = 768;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#f5f5f5';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#1890ff';
        ctx.fillRect(40, 40, canvas.width - 80, canvas.height - 80);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 48px system-ui, -apple-system, Segoe UI, Roboto, Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('导入测试照片', canvas.width / 2, canvas.height / 2 - 30);
        ctx.font = 'normal 28px system-ui, -apple-system, Segoe UI, Roboto, Arial';
        ctx.fillText(dayjs().format('YYYY-MM-DD HH:mm:ss'), canvas.width / 2, canvas.height / 2 + 25);
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
        const buf = await blob.arrayBuffer();
        const u8 = new Uint8Array(buf);
        await injectImageBytes(u8);
    };

    // 将桥接方法挂到 window，并监听 iframe 的消息请求
    useEffect(() => {
        if (!showDrawing) return;

        const onMessage = async (ev) => {
            const data = ev?.data;
            if (!data || typeof data !== 'object') return;
            if (data.type === 'hd:import-scene-image') {
                handleImportPhotoClick();
            } else if (data.type === 'hd:import-scene-image-url' && data.url) {
                try {
                    const resp = await fetch(data.url, { mode: 'cors' });
                    const blob = await resp.blob();
                    const buf = await blob.arrayBuffer();
                    const u8 = new Uint8Array(buf);
                    await injectImageBytes(u8);
                } catch (e) {
                    message.error('从URL导入失败');
                }
            } else if (data.type === 'hd:import-scene-image-bytes' && data.bytes_b64) {
                try {
                    const bin = atob(data.bytes_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    await injectImageBytes(u8);
                } catch (e) {
                    message.error('导入字节流失败');
                }
            } else if (data.type === 'hd:close-hd') {
                // 忽略 hdRequestRelease 之后 1200ms 内的延迟事件（属于上一会话的释放回调）
                if (hdInitOk && (Date.now() - hdInitCompleteTimeRef.current > 1000))
                    handleCloseDrawing({ reason: data.reason || '' });
            }
        };

        window.addEventListener('message', onMessage);
        // 暴露便捷方法
        window.hdHostSelectAndAppendSceneImage = handleImportPhotoClick;
        window.hdHostAppendSceneImageTest = handleImportTest;

        return () => {
            window.removeEventListener('message', onMessage);
            if (window.hdHostSelectAndAppendSceneImage) delete window.hdHostSelectAndAppendSceneImage;
            if (window.hdHostAppendSceneImageTest) delete window.hdHostAppendSceneImageTest;
        };
    }, [showDrawing, hdInitOk]);

    // 监听 native ready 消息，自动初始化
    useEffect(() => {
        // 只有当 iframe 已创建并开始加载，且有 accidentId 时才执行
        if (!iframeReady || !currentAccidentId) return;

        let mounted = true;

        const waitAndInit = async () => {
            try {

                console.log('[AccidentFiling] 开始等待现场绘图就绪...');

                // 等待 native ready
                if (typeof window.hdWhenNativeReady === 'function') {
                    await window.hdWhenNativeReady();
                }

                console.log('[AccidentFiling] WASM 已就绪');

                if (!mounted) return;

                // 自动调用 hdInit（token 由 handydoc-host.js 内部管理）
                const res = await window.hdInit(String(currentAccidentId));
                console.log('[AccidentFiling] auto hdInit result:', res);

                if (!mounted) return;

                if (!res || res.rc !== 0) {
                    message.error('初始化失败 rc=' + (res?.rc ?? '??'));
                    return;
                }

                // 如果有保存的zip数据，导入它
                if (savedZipData && typeof window.hdImportPackageZip === 'function') {
                    try {
                        // 将base64转为Uint8Array
                        const bin = atob(savedZipData);
                        const u8 = new Uint8Array(bin.length);
                        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);

                        const importRes = await window.hdImportPackageZip(String(currentAccidentId), u8);
                        console.log('[AccidentFiling] import zip result:', importRes);
                        if (importRes?.success) {
                            // 不弹出“已加载之前的绘图数据”
                        }
                    } catch (e) {
                        console.error('[AccidentFiling] import zip failed:', e);
                        message.warning('加载之前的数据失败: ' + e.message);
                    }
                }

                // 始终同步表单数据到 WASM（覆盖或初始化字段）
                const values = form.getFieldsValue();
                const push = async (key, value) => {
                    if (!value || typeof window.hdSetInfo !== 'function') return;
                    const r = await window.hdSetInfo(String(currentAccidentId), key, String(value));
                    if (!r || r.rc !== 0) console.warn('hdSetInfo 失败', key, r);
                };
                // 按 WASM 接口要求传递本地 ISO 8601（不带时区与毫秒）：YYYY-MM-DDTHH:mm:ss
                await push(
                    'accidentTime',
                    values.arrive_time
                        ? (dayjs.isDayjs(values.arrive_time)
                            ? values.arrive_time.format('YYYY-MM-DDTHH:mm:ss')
                            : dayjs(values.arrive_time).format('YYYY-MM-DDTHH:mm:ss'))
                        : null
                );
                await push('accidentLocation', values.location);
                await push('weather', values.weather);
                await push('roadSurfaceType', values.road_surface);
                await push('investigatorName', values.investigator);
                await push('drafterName', values.drawer);
                await push(
                    'finalizedAt',
                    values.draw_time
                        ? (dayjs.isDayjs(values.draw_time)
                            ? values.draw_time.format('YYYY-MM-DDTHH:mm:ss')
                            : dayjs(values.draw_time).format('YYYY-MM-DDTHH:mm:ss'))
                        : null
                );
                await push('remarks', values.explan);

                if (!mounted) return;

                setHdInitOk(true);
                hdInitCompleteTimeRef.current = Date.now(); // 记录 hdInit 完成时间，用于过滤旧 session 的 hd:close-hd 事件

                // 调用 WASM 显示导入图片按钮
                if (typeof window.hdSetShowImportImageButton === 'function') {
                    try {
                        await window.hdSetShowImportImageButton(String(currentAccidentId), true);
                        console.log('[AccidentFiling] hdSetShowImportImageButton called');
                    } catch (e) {
                        console.warn('[AccidentFiling] hdSetShowImportImageButton failed:', e);
                    }
                }
                // 调用 WASM 显示标题栏
                if (typeof window.hdSetShowTitleBar === 'function') {
                    try {
                        await window.hdSetShowTitleBar(String(currentAccidentId), true);
                        console.log('[AccidentFiling] hdSetShowTitleBar called');
                    } catch (e) {
                        console.warn('[AccidentFiling] hdSetShowTitleBar failed:', e);
                    }
                }

                // 不弹出"现场绘图已就绪"提示
            } catch (e) {
                console.error('[AccidentFiling] auto init failed:', e);
                if (mounted) {
                    message.error('初始化异常: ' + e.message);
                }
            }
        };

        waitAndInit();

        return () => { mounted = false; };
    }, [iframeReady, currentAccidentId, form, savedZipData]);


    // 获取当前用户信息
    useEffect(() => {
        let mounted = true;
        authService.getCurrentUser().then(res => {
            if (!mounted) return;
            if (res.data && res.data.code === 200) {
                setCurrentUser(res.data.data);
            }
        }).catch(() => {
            // ignore
        });
        return () => { mounted = false; };
    }, []);

    // 加载勘查员、绘图员、天气、路面性质字典
    useEffect(() => {
        if (!currentUser) return;

        const loadDictionaries = async () => {
            try {
                // 勘查员
                const investigatorRes = await dictionaryService.getAllDictionaries('investigator', currentUser.user_unit_id);
                if (investigatorRes.data.code === 200) {
                    setInvestigators(investigatorRes.data.data.map(item => ({
                        value: item.name,
                        label: item.name
                    })));
                }
                // 绘图员
                const drawerRes = await dictionaryService.getAllDictionaries('drawer', currentUser.user_unit_id);
                if (drawerRes.data.code === 200) {
                    setDrawers(drawerRes.data.data.map(item => ({
                        value: item.name,
                        label: item.name
                    })));
                }
                // 天气
                const weatherRes = await dictionaryService.getAllDictionaries('weather', currentUser.user_unit_id);
                if (weatherRes.data.code === 200) {
                    setWeatherOptions(weatherRes.data.data.map(item => ({
                        value: item.name,
                        label: item.name
                    })));
                }
                // 路面性质
                const roadSurfaceRes = await dictionaryService.getAllDictionaries('road_surface', currentUser.user_unit_id);
                if (roadSurfaceRes.data.code === 200) {
                    setRoadSurfaceOptions(roadSurfaceRes.data.data.map(item => ({
                        value: item.name,
                        label: item.name
                    })));
                }
            } catch (error) {
                console.error('加载字典失败:', error);
            }
        };

        loadDictionaries();
    }, [currentUser]);

    // 打开现场绘图（每次都完整重新加载 WASM）
    const handleOpenDrawing = async () => {
        console.log('[AccidentFiling] ========== handleOpenDrawing 开始 ==========');
        const accidentId = form.getFieldValue('accident_id');
        console.log('[AccidentFiling] accidentId:', accidentId);
        if (!accidentId) {
            message.warning('请先填写事故ID');
            return;
        }
        console.log('[AccidentFiling] 设置状态: currentAccidentId, showDrawing=true');
        closeDialogRef.current = false; // 新会话开始，重置对话框状态
        setCurrentAccidentId(accidentId);
        setShowDrawing(true);
        setHdInitOk(false);
        console.log('[AccidentFiling] ========== handleOpenDrawing 结束 ==========');

        // iframe 会在 useEffect 中创建并自动加载 WASM
        // 如果有保存的zip，在初始化后加载
    };

    // 关闭绘图工具，询问是否保存
    const handleCloseDrawing = async ({ reason = '' } = {}) => {
        const isForceClose = reason === 'fatal_error';
        if (closeDialogRef.current) return; // 已有对话框在显示，防止重复弹出
        if (!hdInitOk || !currentAccidentId) {
            setShowDrawing(false);
            return;
        }
        closeDialogRef.current = true;

        // 保存逻辑函数
        const saveDrawing = async () => {
            try {
                setIsLoading(true);
                setLoadingText('正在保存...');

                // 1. 先从 WASM 获取最新字段信息并同步到表单
                try {
                    setLoadingText('同步字段信息...');

                    // hdGetInfo 需要逐个字段调用
                    const wasmInfo = {};

                    // 获取各个字段
                    const fields = [
                        { wasmKey: 'accidentTime', formKey: 'arrive_time', isDate: true },
                        { wasmKey: 'accidentLocation', formKey: 'location' },
                        { wasmKey: 'weather', formKey: 'weather' },
                        { wasmKey: 'roadSurfaceType', formKey: 'road_surface' },
                        { wasmKey: 'investigatorName', formKey: 'investigator' },
                        { wasmKey: 'drafterName', formKey: 'drawer' },
                        { wasmKey: 'finalizedAt', formKey: 'draw_time', isDate: true },
                        { wasmKey: 'remarks', formKey: 'explan' }
                    ];

                    for (const field of fields) {
                        try {
                            const res = await window.hdGetInfo(String(currentAccidentId), field.wasmKey);
                            console.log(`[AccidentFiling] hdGetInfo(${field.wasmKey}):`, res);
                            if (res?.success && res.value) {
                                wasmInfo[field.formKey] = res.value;
                            } else {
                                console.warn(`[AccidentFiling] ${field.wasmKey} 返回空值或失败:`, res);
                            }
                        } catch (e) {
                            console.warn(`获取 ${field.wasmKey} 失败:`, e);
                        }
                    }

                    console.log('[AccidentFiling] WASM字段信息:', wasmInfo);

                    // 构建要更新的字段
                    const updatedFields = {};

                    if (wasmInfo.arrive_time) {
                        updatedFields.arrive_time = dayjs(wasmInfo.arrive_time);
                    }
                    if (wasmInfo.location) {
                        updatedFields.location = wasmInfo.location;
                    }
                    if (wasmInfo.weather) {
                        updatedFields.weather = wasmInfo.weather;
                    }
                    if (wasmInfo.road_surface) {
                        updatedFields.road_surface = wasmInfo.road_surface;
                    }
                    if (wasmInfo.investigator) {
                        updatedFields.investigator = wasmInfo.investigator;
                    }
                    if (wasmInfo.drawer) {
                        updatedFields.drawer = wasmInfo.drawer;
                    }
                    if (wasmInfo.draw_time) {
                        updatedFields.draw_time = dayjs(wasmInfo.draw_time);
                    }
                    if (wasmInfo.explan) {
                        updatedFields.explan = wasmInfo.explan;
                    }

                    console.log('[AccidentFiling] 准备更新表单字段:', updatedFields);

                    // 更新表单字段（精简为一次性设置）
                    if (Object.keys(updatedFields).length > 0) {
                        form.setFieldsValue(updatedFields);
                        // 去除“字段信息已同步”提示
                    } else {
                        // 去除“没有可同步的字段”提示
                    }
                } catch (e) {
                    console.error('[AccidentFiling] 获取WASM字段信息失败:', e);
                    message.warning('同步字段信息失败: ' + e.message);
                }

                // 导出所有类型的现场图
                const images = {};
                const imageFiles = {};

                // 1. 竖版 (type=0, exportFormat=0)
                setLoadingText('导出竖版图纸...');
                const verticalRes = await window.hdGetAccidentSceneDiagram(String(currentAccidentId), 0, 0);
                if (verticalRes?.success && verticalRes.value_b64) {
                    images.vertical = `data:${verticalRes.mime || 'image/jpeg'};base64,${verticalRes.value_b64}`;
                    // 转换为File对象
                    const bin = atob(verticalRes.value_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    const blob = new Blob([u8], { type: verticalRes.mime || 'image/jpeg' });
                    imageFiles.vertical = new File([blob], `vertical-${currentAccidentId}.jpg`, { type: verticalRes.mime || 'image/jpeg' });
                }

                // 2. 横版 (type=1, exportFormat=0)
                setLoadingText('导出横版图纸...');
                const horizontalRes = await window.hdGetAccidentSceneDiagram(String(currentAccidentId), 1, 0);
                if (horizontalRes?.success && horizontalRes.value_b64) {
                    images.horizontal = `data:${horizontalRes.mime || 'image/jpeg'};base64,${horizontalRes.value_b64}`;
                    // 转换为File对象
                    const bin = atob(horizontalRes.value_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    const blob = new Blob([u8], { type: horizontalRes.mime || 'image/jpeg' });
                    imageFiles.horizontal = new File([blob], `horizontal-${currentAccidentId}.jpg`, { type: horizontalRes.mime || 'image/jpeg' });
                    // 横版作为默认预览图提交给后端（保持兼容性）
                    setFiles(prev => ({ ...prev, preview: imageFiles.horizontal }));
                }

                // 3. 实景记录图 (type=2, exportFormat=0)
                setLoadingText('导出实景记录图...');
                const sceneRes = await window.hdGetAccidentSceneDiagram(String(currentAccidentId), 2, 0);
                if (sceneRes?.success && sceneRes.value_b64) {
                    images.scene = `data:${sceneRes.mime || 'image/jpeg'};base64,${sceneRes.value_b64}`;
                    // 转换为File对象
                    const bin = atob(sceneRes.value_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    const blob = new Blob([u8], { type: sceneRes.mime || 'image/jpeg' });
                    imageFiles.scene = new File([blob], `scene-${currentAccidentId}.jpg`, { type: sceneRes.mime || 'image/jpeg' });
                }

                // 4. 实景俯瞰记录图 (type=3, exportFormat=0)
                setLoadingText('导出实景俯瞰记录图...');
                const overheadRes = await window.hdGetAccidentSceneDiagram(String(currentAccidentId), 3, 0);
                if (overheadRes?.success && overheadRes.value_b64) {
                    images.overhead = `data:${overheadRes.mime || 'image/jpeg'};base64,${overheadRes.value_b64}`;
                    // 转换为File对象
                    const bin = atob(overheadRes.value_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    const blob = new Blob([u8], { type: overheadRes.mime || 'image/jpeg' });
                    imageFiles.overhead = new File([blob], `overhead-${currentAccidentId}.jpg`, { type: overheadRes.mime || 'image/jpeg' });
                }

                setPreviewImages(images);

                // 保存所有图片文件到files状态
                setFiles(prev => ({
                    ...prev,
                    preview: imageFiles.horizontal,  // 保持兼容性
                    vertical: imageFiles.vertical,
                    horizontal: imageFiles.horizontal,
                    scene: imageFiles.scene,
                    overhead: imageFiles.overhead
                }));

                // 导出zip包
                setLoadingText('导出数据包...');
                const zipRes = await window.hdExportPackageZip(String(currentAccidentId));
                if (zipRes?.success && zipRes.value_b64) {
                    // 保存zip的base64数据（仅用于本次提交），保存后立即清空，避免后续自动导入
                    setSavedZipData(zipRes.value_b64);
                    // 转换为Blob用于提交
                    const bin = atob(zipRes.value_b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    const blob = new Blob([u8], { type: zipRes.mime || 'application/zip' });
                    const file = new File([blob], `dataPackage-${currentAccidentId}.zip`, { type: zipRes.mime || 'application/zip' });
                    setFiles(prev => ({ ...prev, dataPackage: file }));
                    // 不弹出其它保存成功提示
                }

                message.success('绘图数据已保存');
                setSavedZipData(null);
                // 释放服务端并发席位
                if (typeof window.hdRequestRelease === 'function') {
                    try { await window.hdRequestRelease(1000); } catch (e) { console.warn('[AccidentFiling] hdRequestRelease failed:', e); }
                }
                setShowDrawing(false);
            } catch (e) {
                console.error('[AccidentFiling] save failed:', e);
                message.error('保存失败: ' + e.message);
            } finally {
                setIsLoading(false);
            }
        };

        // 弹出确认对话框 - 正常操作三个按钮：保存、不保存、取消；会话失效时隐藏取消
        const modal = Modal.warning({
            title: isForceClose ? '会话已过期' : '保存现场绘图',
            content: isForceClose ? '绘图会话已断开，请选择是否保存当前数据。' : '是否保存当前的现场绘图数据？',
            icon: null,
            footer: (_, { OkBtn }) => (
                <>
                    {!isForceClose && (
                        <Button onClick={() => { closeDialogRef.current = false; modal.destroy(); }}>取消</Button>
                    )}
                    <Button danger onClick={async () => {
                        modal.destroy();
                        closeDialogRef.current = false;
                        if (typeof window.hdRequestRelease === 'function') {
                            try { await window.hdRequestRelease(1000); } catch (e) { console.warn('[AccidentFiling] hdRequestRelease failed:', e); }
                        }
                        setShowDrawing(false);
                        message.info('已放弃保存');
                    }}>
                        不保存
                    </Button>
                    <Button type="primary" onClick={() => {
                        modal.destroy();
                        saveDrawing();
                    }}>
                        保存
                    </Button>
                </>
            ),
        });
    };

    const handleSubmit = async (values) => {
        // 弹出确认对话框
        Modal.confirm({
            title: '确认提交',
            content: '确定要提交这条事故立案记录吗？',
            okText: '确定',
            cancelText: '取消',
            onOk: async () => {
                try {
                    setLoading(true);

                    // 检查事故ID是否已存在
                    try {
                        const checkResponse = await accidentService.getAccidentByAccidentId(values.accident_id);
                        if (checkResponse.data.code === 200 && checkResponse.data.data) {
                            message.error(`事故ID "${values.accident_id}" 已存在，请使用其他ID或前往事故查询页面编辑该事故`);
                            setLoading(false);
                            return;
                        }
                    } catch (error) {
                        // 404说明不存在，可以继续创建
                        if (error.response?.status !== 404) {
                            throw error;
                        }
                    }

                    const formData = new FormData();
                    formData.append('accident_id', values.accident_id);
                    formData.append('accident_time', values.accident_time.format('YYYY-MM-DD HH:mm:ss'));
                    formData.append('arrive_time', values.arrive_time ? values.arrive_time.format('YYYY-MM-DD HH:mm:ss') : '');
                    formData.append('location', values.location);
                    formData.append('weather', values.weather || '');
                    formData.append('road_surface', values.road_surface || '');
                    formData.append('investigator', values.investigator || '');
                    formData.append('draw_time', values.draw_time ? values.draw_time.format('YYYY-MM-DD HH:mm:ss') : '');
                    formData.append('explan', values.explan || '');

                    if (files.preview) {
                        formData.append('previewImage', files.preview);
                    }
                    if (files.vertical) {
                        formData.append('verticalImage', files.vertical);
                    }
                    if (files.horizontal) {
                        formData.append('horizontalImage', files.horizontal);
                    }
                    if (files.scene) {
                        formData.append('sceneImage', files.scene);
                    }
                    if (files.overhead) {
                        formData.append('overheadImage', files.overhead);
                    }
                    if (files.dataPackage) {
                        formData.append('dataPackage', files.dataPackage);
                    }

                    const response = await accidentService.createOrUpdateAccident(formData);
                    if (response.data.code === 200) {
                        message.success(response.data.message);
                        // 重置所有字段，事故发生时间和到达事故现场时间都设为当前时间
                        form.resetFields();
                        setFiles({});
                        setPreviewImages({
                            vertical: null,
                            horizontal: null,
                            scene: null,
                            overhead: null
                        });
                        setSavedZipData(null);
                        form.setFieldsValue({
                            accident_id: Date.now().toString(),
                            accident_time: dayjs(),
                            arrive_time: dayjs(),
                            weather: '晴',
                            road_surface: '沥青'
                        });
                    }
                } catch (error) {
                    message.error(error.response?.data?.message || '操作失败');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    return (
        <div className="accident-filing">
            <Card title="事故立案" className="filing-card">
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {/* 隐藏字段：绘图时间（由 WASM 保存后回填），为确保提交时包含该值，这里注册为隐藏项 */}
                    <Form.Item name="draw_time" hidden>
                        <DatePicker showTime format="YYYY年MM月DD日 HH时mm分" />
                    </Form.Item>
                    <Row gutter={24}>
                        <Col xs={24} sm={12} md={6} lg={6} xl={6}>
                            <Form.Item label="事故ID" name="accident_id" rules={[{ required: true, message: '请输入事故ID' }]}>
                                <Input placeholder="请输入事故ID" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={5} lg={5} xl={5}>
                            <Form.Item label="事故发生时间" name="accident_time" rules={[{ required: true, message: '请选择事故发生时间' }]}>
                                <DatePicker showTime format="YYYY年MM月DD日 HH时mm分" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={5} lg={5} xl={5}>
                            <Form.Item label="到达事故现场时间" name="arrive_time" rules={[{ required: true, message: '请选择到达事故现场时间' }]}>
                                <DatePicker showTime format="YYYY年MM月DD日 HH时mm分" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                            <Form.Item label="天气" name="weather">
                                <Select placeholder="请选择天气" style={{ width: '100%' }}>
                                    {weatherOptions.map(opt => (
                                        <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                            <Form.Item label="路面性质" name="road_surface">
                                <Select placeholder="请选择路面性质" style={{ width: '100%' }}>
                                    {roadSurfaceOptions.map(opt => (
                                        <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                            <Form.Item label="事故地点" name="location" rules={[{ required: true, message: '请输入事故地点' }]}>
                                <Input placeholder="请输入事故地点" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                            <Form.Item label="勘查员" name="investigator">
                                <AutoComplete
                                    options={investigators}
                                    placeholder="请选择或输入勘查员"
                                    filterOption={(inputValue, option) =>
                                        option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                            <Form.Item label="绘图员" name="drawer">
                                <AutoComplete
                                    options={drawers}
                                    placeholder="请选择或输入绘图员"
                                    filterOption={(inputValue, option) =>
                                        option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col xs={24}>
                            <Form.Item label="事故说明" name="explan">
                                <Input.TextArea rows={6} placeholder="请输入事故说明" autoSize={{ minRows: 4, maxRows: 12 }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* 现场图 - 合并显示和绘制功能 */}
                    <Row gutter={24}>
                        <Col xs={24}>
                            <Form.Item label="现场图">
                                {(previewImages.vertical || previewImages.horizontal || previewImages.scene || previewImages.overhead) ? (
                                    <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 16, background: '#fafafa', minHeight: '600px' }}>
                                        <Tabs
                                            animated={false}
                                            destroyInactiveTabPane={false}
                                            defaultActiveKey="horizontal"
                                            tabBarExtraContent={
                                                <Button
                                                    type="primary"
                                                    icon={<PictureOutlined />}
                                                    onClick={handleOpenDrawing}
                                                    size="small"
                                                >
                                                    绘制现场图
                                                </Button>
                                            }
                                            items={[
                                                {
                                                    key: 'vertical',
                                                    label: '竖版图纸',
                                                    children: previewImages.vertical ? (
                                                        <div style={{ textAlign: 'center' }}>
                                                            <img
                                                                src={previewImages.vertical}
                                                                alt="竖版图纸"
                                                                style={{ maxWidth: '100%', maxHeight: 500, display: 'inline-block' }}
                                                            />
                                                        </div>
                                                    ) : <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>暂无竖版图纸</div>
                                                },
                                                {
                                                    key: 'horizontal',
                                                    label: '横版图纸',
                                                    children: previewImages.horizontal ? (
                                                        <div style={{ textAlign: 'center' }}>
                                                            <img
                                                                src={previewImages.horizontal}
                                                                alt="横版图纸"
                                                                style={{ maxWidth: '100%', maxHeight: 500, display: 'inline-block' }}
                                                            />
                                                        </div>
                                                    ) : <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>暂无横版图纸</div>
                                                },
                                                {
                                                    key: 'scene',
                                                    label: '实景记录图',
                                                    children: previewImages.scene ? (
                                                        <div style={{ textAlign: 'center' }}>
                                                            <img
                                                                src={previewImages.scene}
                                                                alt="实景记录图"
                                                                style={{ maxWidth: '100%', maxHeight: 500, display: 'inline-block' }}
                                                            />
                                                        </div>
                                                    ) : <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>暂无实景记录图</div>
                                                },
                                                {
                                                    key: 'overhead',
                                                    label: '实景俯瞰记录图',
                                                    children: previewImages.overhead ? (
                                                        <div style={{ textAlign: 'center' }}>
                                                            <img
                                                                src={previewImages.overhead}
                                                                alt="实景俯瞰记录图"
                                                                style={{ maxWidth: '100%', maxHeight: 500, display: 'inline-block' }}
                                                            />
                                                        </div>
                                                    ) : <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>暂无实景俯瞰记录图</div>
                                                }
                                            ]}
                                        />
                                    </div>
                                ) : (
                                    <div style={{
                                        border: '1px dashed #d9d9d9',
                                        borderRadius: 4,
                                        padding: '40px 16px',
                                        background: '#fafafa',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ marginBottom: 16, color: '#999', fontSize: 14 }}>
                                            暂无现场图，请点击下方按钮绘制
                                        </div>
                                        <Button
                                            type="primary"
                                            icon={<PictureOutlined />}
                                            onClick={handleOpenDrawing}
                                            size="large"
                                        >
                                            绘制现场图
                                        </Button>
                                    </div>
                                )}
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row justify="end">
                        <Col>
                            <Space size="middle">
                                <Button
                                    size="large"
                                    onClick={() => {
                                        // 使用与页面初始化相同的逻辑
                                        form.setFieldsValue({
                                            accident_id: Date.now().toString(), // 当前时间戳（毫秒）
                                            accident_time: dayjs(), // 当前时间
                                            arrive_time: dayjs(), // 到达事故现场时间默认当前时间
                                            weather: '晴',
                                            road_surface: '沥青'
                                        });
                                        // 清空其他字段
                                        form.setFieldsValue({
                                            location: undefined,
                                            investigator: undefined,
                                            drawer: undefined,
                                            explan: undefined
                                        });
                                        setFiles({});
                                        setPreviewImages({
                                            vertical: null,
                                            horizontal: null,
                                            scene: null,
                                            overhead: null
                                        });
                                        setSavedZipData(null);
                                        message.success('表单已重置');
                                    }}
                                >
                                    重 置
                                </Button>
                                <Button type="primary" htmlType="submit" loading={loading} size="large">
                                    提 交
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </Card>

            {/* 道路交通事故现场绘图 Modal */}
            <Modal
                title={null}
                closable={false}
                open={showDrawing}
                onCancel={() => handleCloseDrawing()}
                afterOpenChange={handleModalAfterOpenChange}
                width="100vw"
                centered={false}
                style={{
                    top: 0,
                    left: 0,
                    maxWidth: '100vw',
                    paddingBottom: 0,
                    margin: 0,
                    padding: 0
                }}
                styles={{
                    body: {
                        padding: 0,
                        margin: 0,
                        overflow: 'hidden'
                    },
                    content: {
                        padding: 0,
                        margin: 0,
                        borderRadius: 0
                    }
                }}
                footer={null}
                wrapClassName="fullscreen-modal"
            >
                {/* 隐藏的文件选择器 - 保留给 WASM 调用 */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileInputChange}
                />

                {/* 加载提示：水平居中，垂直1/3处 */}
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: '33.33%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1000,
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '20px 40px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        fontSize: '16px',
                        color: '#1890ff',
                        fontWeight: 500
                    }}>
                        {loadingText}
                    </div>
                )}

                {/* iframe 容器：组件挂载时自动创建并插入 iframe */}
                <div
                    ref={iframeContainerRef}
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        margin: 0,
                        padding: 0,
                        zIndex: 1,
                        background: '#fff',
                        overflow: 'hidden'
                    }}
                />
            </Modal>
        </div>
    );
};

export default AccidentFiling;
